import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import useStore from '@/store/useStore';
import Button from '@/components/homepage/Button/Button';
import RichText from '@/components/RichText/RichText';
import { getComponentMargin } from '@/utils/builderUtils';
import beTwMerge from '@/utils/beTwMerge';
import { useBuilderProvider } from '@/context/BuilderContext';
import useMediaQuery from '@/hooks/useMediaQuery';
import { IDataShowroom, ShowroomStatusOptions } from '../CommonFields';

interface IShowroom {
  name: string;
  data: IDataShowroom;
}
interface IStore {
  value: IShowroom;
}
export interface IStoreInfoBlock {
  store?: IStore;
  margin?: boolean;
}

export default function StoreInfoBlock({ store, margin }: IStoreInfoBlock) {
  const { device } = useBuilderProvider();
  const isMobile = useMediaQuery('(max-width: 768px)', device === 'mobile');
  const isDesktop = useMediaQuery('(min-width: 1024px)', device === 'desktop');
  const setShowroomModalInfo = useStore(state => state.setAppointmentBookingInformation);
  const setShowroomCityId = useStore(state => state.setShowroomCityId);
  const [extraCityDetails, setExtraCityDetails] = useState({ id: '', timezoneId: '', timezoneAbreviation: '' });
  const setIsAppointmentBookingModalVisible = useStore(state => state.setIsAppointmentBookingModalVisible);
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isIphone, setIsIphone] = useState(false);
  const richRef = useRef<HTMLDivElement>(null);

  const timezones = {
    PST: 'PT', // Pacific Standard Time
    PDT: 'PT', // Pacific Daylight Time
    EST: 'ET', // Eastern Standard Time
    EDT: 'ET', // Eastern Daylight Time
    CST: 'CT', // Central Standard Time
    CDT: 'CT', // Central Daylight Time
    MST: 'MT', // Mountain Standard Time
    MDT: 'MT', // Mountain Daylight Time
    AKST: 'AKT', // Alaska Standard Time
    AKDT: 'AKT', // Alaska Daylight Time
    HST: 'HST', // Hawaii-Aleutian Standard Time (Hawaii does not observe DST)
  };

  useEffect(() => {
    async function setCityId() {
      const cityDetails = await setShowroomCityId(store?.value?.data);
      setExtraCityDetails(cityDetails);
    }

    setCityId();
  }, []);

  useEffect(() => {
    const handleClick = () => {
      setIsTextExpanded(false);
      (window as any).dataLayer.push({
        event: 'showroom_page',
        showroom_action: 'read less',
        showroom_page_section: 'banner card',
      });
    };

    let element;
    setTimeout(() => {
      element = richRef.current?.querySelector('.readLess');
      if (element) {
        element.addEventListener('click', handleClick);
      }
    }, 2000);

    return () => {
      if (element) {
        element.removeEventListener('click', handleClick);
      }
    };
  }, [richRef, isMobile]);

  const expandText = () => {
    (window as any).dataLayer.push({
      event: 'showroom_page',
      showroom_action: 'read more',
      showroom_page_section: 'banner card',
    });
    setIsTextExpanded(!isTextExpanded);
  };

  const bookAppointment = () => {
    (window as any).dataLayer.push({
      event: 'showroom_page',
      showroom_action: 'book setster appointment',
      showroom_page_section: 'banner card',
    });

    const storeData = {
      modalName: `${store?.value.data.displayName1}${store?.value.data.displayName2 ? ` - ${store?.value.data.displayName2}` : ''}`,
      displayName1: store?.value.data.displayName1 || '',
      displayName2: store?.value.data.displayName2 || '',
      addresLine1: store?.value.data.addressLine1 || '',
      addressLine2: store?.value.data.addressLine2 || '',
      setsterLocationId: store?.value.data.setsterLocationId || '',
      id: extraCityDetails.id || '',
      timezoneName: extraCityDetails.timezoneId,
      timezoneAbreviation: timezones[extraCityDetails.timezoneAbreviation],
      timezoneId: store?.value.data.timezoneId || '',
      city: store?.value.data.city || '',
      state: store?.value.data.state || '',
      zipCode: store?.value.data.zipCode || '',
    };

    setShowroomModalInfo('prevSelectedShowroomStore', storeData);
    setShowroomModalInfo('selectedShowroomStore', storeData);
    setShowroomModalInfo('activePage', 2);

    setIsAppointmentBookingModalVisible(true);
  };

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsIphone(() => /iPad|iPhone|iPod/.test(navigator.userAgent));
    }
  }, []);

  const getLinkMap = () => {
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${store?.value?.data?.latitude},${store?.value?.data?.longitude}&query_place_id=${store?.value?.data?.googlePlacesId}`;

    return googleMapsUrl;
  };

  const openMap = () => {
    (window as any).dataLayer.push({
      event: 'showroom_page',
      showroom_action: 'address map click',
      showroom_page_section: 'banner card',
    });
  };

  const linkMap = getLinkMap();

  const getHeader = () => {
    if (store?.value?.data?.storeInfoBlockHeroHeader) {
      return (
        <div className="">
          <h1 className="tw-m-0 tw-font-ebgaramond tw-text-[#2C2D2D] tw-text-[2rem] lg-tw-text-[30px] min-[1200px]:tw-text-[40px] tw-capitalize tw-not-italic tw-leading-[normal] tw-self-stretch">
            {store?.value?.data?.storeInfoBlockHeroHeader}
          </h1>
        </div>
      );
    }
    if (store?.value?.data?.displayName1 && store?.value?.data?.displayName2) {
      return (
        <h1 className="tw-font-ebgaramond tw-flex tw-flex-col tw-m-0 tw-font-normal tw-not-italic tw-uppercase tw-leading-normal lg:tw-mt-[20px] min-[1200px]:tw-text-[13px]">
          <span className="tw-tracking-[0.65px] lg:tw-tracking-[0.7px] tw-text-[13px] lg:tw-text-[14px]">{`${store?.value?.data?.displayName2} `}</span>
          <span className="tw-text-[2rem] tw-tracking-normal tw-capitalize lg-tw-text-[30px] min-[1200px]:tw-text-[40px]">
            {store?.value?.data?.displayName1}
          </span>
        </h1>
      );
    }

    return (
      <div className="lg:tw-mt-[20px]">
        <p className="tw-mb-0 tw-uppercase tw-tracking-[0.65px] lg:tw-tracking-[0.7px] tw-text-[13px] lg:tw-text-[14px]">
          {store?.value?.data?.displayName1}
        </p>
        <h1 className="tw-font-ebgaramond tw-flex tw-flex-col tw-m-0 !tw-text-[13px] tw-font-normal tw-not-italic tw-uppercase tw-leading-normal min-[1200px]:tw-text-[13px]">
          <span className="tw-text-[2rem] tw-font-medium tw-tracking-normal tw-capitalize lg-tw-text-[30px] min-[1200px]:tw-text-[40px]">
            {store?.value?.data?.displayName1}
          </span>
        </h1>
      </div>
    );
  };

  const addressInfo = () => {
    if (
      store?.value?.data?.addressLine1 ||
      store?.value?.data?.addressLine2 ||
      store?.value?.data?.city ||
      store?.value?.data?.state ||
      store?.value?.data?.zipCode
    ) {
      return (
        <a href={linkMap} target="_blank" rel="noreferrer" onClick={openMap} className="tw-inline-block tw-font-medium">
          {store?.value?.data?.addressLine1 || store?.value?.data?.addressLine2 ? (
            <p className="tw-font-['Avenir_Book'] tw-font-[350] tw-m-0 tw-text-[14px] tw-not-italic tw-leading-normal tw-underline lg:tw-text-[1rem]">
              {`${store?.value?.data?.addressLine1 || ''} ${store?.value?.data?.addressLine2 || ''}`}
            </p>
          ) : null}

          {store?.value?.data?.city || store?.value?.data?.state || store?.value?.data?.zipCode ? (
            <p className="tw-font-['Avenir_Book'] tw-font-[350] tw-m-0 tw-text-[14px] tw-not-italic tw-leading-normal tw-underline lg:tw-text-[1rem]">
              {`${store?.value?.data?.city || ''}, ${store?.value?.data?.state || ''} ${store?.value?.data?.zipCode || ''}`}
            </p>
          ) : null}
        </a>
      );
    }
  };

  const getDescription = () => {
    const countText =
      store?.value?.data?.storeDescriptionText.trim().replace(/\n/g, '').split('<p>').filter(Boolean).length || 0;

    const lastParagraph = store?.value?.data?.storeDescriptionText
      .trim()
      .replace(/\n/g, '')
      .split('<p>')
      .slice(-1)
      .join('')
      .replace('<p>', '')
      .replace('</p>', '');

    const linkText = `<a class='readLess tw-not-italic hover:tw-cursor-pointer tw-underline tw-font-["Avenir_Book"] !tw-font-[350]'>Read Less</a>`;
    const textWithLink = `<p style="text-wrap: pretty;">${lastParagraph} ${linkText}</p>`;

    if (isMobile && countText === 1) {
      return textWithLink;
    }

    if (isMobile && countText > 0) {
      return `${store?.value?.data?.storeDescriptionText
        .trim()
        .replace(/\n/g, '')
        .split('<p>')
        .filter(Boolean)
        .slice(0, -1)
        .map(item => `<p style="text-wrap: pretty;">${item}`)
        .join('')} ${textWithLink}`;
    }

    return store?.value?.data?.storeDescriptionText || '';
  };

  const onClickHandler =
    store?.value?.data?.status === 'Coming Soon'
      ? () => (window as Window).open(store?.value?.data?.storeInfoBlockHeroComingSoonCtaLink, '_self')
      : bookAppointment;

  return (
    <div
      className={beTwMerge(
        'tw-w-full tw-flex tw-flex-col tw-max-w-[1440px] lg:tw-max-h-[512px] lg:tw-flex-row lg:tw-px-[2rem] min-[1440px]:tw-self-center min-[1504px]:tw-px-0',
        getComponentMargin(margin),
        '[&>picture]:lg:tw-w-[50%]'
      )}
    >
      {store?.value?.data?.locationImage && (
        <>
          <Image
            src={store?.value?.data?.locationImage}
            fill
            priority={isDesktop}
            alt={store?.value?.data?.locationImageAltText || ''}
            sizes="(min-width: 1024px) 50vw"
            className={beTwMerge('!tw-relative lg:!tw-w-1/2 lg:tw-max-h-[512px]', isDesktop ? '' : 'tw-hidden')}
          />
          <Image
            src={store?.value?.data?.mobileLocationImage}
            fill
            priority={!isDesktop}
            alt={store?.value?.data?.locationImageAltText || ''}
            sizes="(max-width: 1023px) 100vw"
            className={beTwMerge('!tw-relative !tw-w-full', isDesktop ? 'tw-hidden' : '')}
          />
        </>
      )}
      <div className="tw-flex tw-flex-col tw-gap-[2rem] lg:tw-gap-[1.5rem] tw-px-[1rem] tw-pt-[1rem] tw-pb-[48px] tw-bg-[#F9F6F5] lg:tw-w-[50%] lg:tw-py-[1rem] lg:tw-px-[1.5rem] min-[1200px]:tw-gap-[2rem] min-[1200px]:tw-px-[3rem] min-[1200px]:tw-py-[13.96px] min-[1440px]:tw-p-[3rem] tw-justify-center">
        <div className="tw-flex tw-flex-col tw-gap-[12px] lg:tw-gap-[1rem] min-[1200px]:tw-gap-[1.5rem]">
          {getHeader()}
          {store?.value?.data?.storeDescriptionText && (
            <div
              ref={richRef}
              className={beTwMerge(
                'tw-relative tw-overflow-hidden desktop:tw-max-h-fit [&_p]:!tw-font-["Avenir_Book"] [&_a]:!tw-font-["Avenir_Book"] [&_p_span]:!tw-text-[14px] lg:[&_p_span]:!tw-text-[1rem]',
                isIphone ? 'tw-max-h-[40px]' : 'tw-max-h-[34px]',
                isTextExpanded ? 'tw-max-h-fit' : ''
              )}
            >
              <RichText
                content={getDescription()}
                className={beTwMerge(
                  '[&]:tw-m-0 [&]:tw-tracking-normal [&]:tw-not-italic [&]:tw-font-["Avenir_Book"] [&]:tw-text-[14px] [&]:lg:tw-text-[1rem] [&]:tw-text-pretty [&>a]:tw-font-[350] [&>p]:tw-font-[350] [&>p]:tw-m-0 [&>p]:tw-tracking-normal [&>p]:tw-not-italic [&>p]:tw-font-["Avenir_Book"] [&>p]:tw-text-[14px] [&>p]:lg:tw-text-[1rem] [&>p]:tw-text-pretty',
                  isTextExpanded ? '[&>p]:tw-max-h-fit' : ''
                )}
              />
              <a
                className={beTwMerge(
                  'tw-absolute tw-top-[19px] tw-right-0 tw-z-50 tw-text-be-sm tw-cursor-pointer tw-underline tw-bg-[#F9F6F5] hover:tw-text-[#0D6363]',
                  'after:tw-content-[""] after:tw-absolute after:tw-right-[100%] after:tw-w-[30px] after:tw-h-[100%] after:tw-top-0 after:tw-block',
                  'after:tw-bg-gradient-to-r after:tw-from-[rgba(255,255,255,0.0392156863)] after:tw-to-[#F9F6F5]',
                  '!tw-font-["Avenir_Book"] tw-not-italic min-[617px]:tw-hidden',
                  isTextExpanded ? 'tw-hidden' : ''
                )}
                onClick={expandText}
                role="button"
                tabIndex={0}
              >
                <span className="tw-inline-block tw-relative tw-bottom-[0.0078125rem]"> ... </span>
                Read More
              </a>
            </div>
          )}

          {store?.value?.data?.status !== 'Coming Soon' && (
            <>
              <div className="tw-hidden lg:tw-block lg:tw-h-[1px] lg:tw-bg-[#D9D9D9]" />

              <div className='tw-flex tw-flex-row tw-gap-3 min-[1200px]:tw-gap-[59px] tw-justify-between [&_p]:!tw-font-["Avenir_Book"] [&_a]:!tw-font-["Avenir_Book"] [&_p_span]:!tw-text-[14px] lg:[&_p_span]:!tw-text-[1rem]'>
                <div className="tw-flex-[1] md:tw-w-[290px]">
                  {addressInfo()}
                  {store?.value?.data?.additionalAddressDescription && (
                    <p className="tw-m-0 tw-text-[14px] tw-not-italic tw-leading-normal lg:tw-text-[1rem] tw-font-book tw-font-[350]">
                      {store?.value?.data?.additionalAddressDescription}
                    </p>
                  )}
                </div>
                {store?.value?.data?.hours && (
                  <div className="tw-flex-[1]">
                    <RichText
                      content={store?.value?.data?.hours}
                      className="tw-m-0 tw-font-book tw-text-be-sm tw-font-[350] lg:tw-text-base tw-not-italic !tw-leading-normal [&>p]:tw-m-0 [&>p]:tw-text-be-sm lg:[&>p]:tw-text-base [&>p]:tw-not-italic [&>p]:tw-font-[350] [&>p]:!tw-leading-normal"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {store && (
          <Button
            label={getLabelButton(store?.value?.data?.status)}
            className="tw-max-w-none lg:tw-max-w-[200px]"
            onClick={onClickHandler}
          />
        )}
      </div>
    </div>
  );
}

const getLabelButton = (label: ShowroomStatusOptions) => {
  switch (label) {
    case 'Open':
      return 'Book Appointment';
    case 'Coming Soon':
      return 'Sign Up for Updates';
    case 'Pre-Book':
      return 'Pre-Book Appointment';
    default:
      return '';
  }
};
